import { computed, ref, watch } from "vue";
import { useSharedState } from "composables/useSharedState";
import type { ChatProps, ChatMessage, ChatMessageGroup } from "./chat.types";
import { resolveLinksForMessage } from "./utils/attachmentUtils";
import { groupMessages } from "./utils/groupMessages";

export interface UseChatMessagesOptions {
  props: Readonly<ChatProps>;
}

export const useChatMessages = (options: UseChatMessagesOptions) => {
  const { props } = options;
  const state = useSharedState(props);

  const internalMessages = ref<ChatMessage[]>(props.messages ? [...props.messages] : []);
  const arrayModeActivated = ref(props.messages != null && props.messages.length > 0);
  const isArrayMode = computed(() => arrayModeActivated.value);
  const currentUser = computed(() => state.currentUser?.value ?? "receiver");
  const linkPreviewCache = new Map<string, boolean>();

  const resolveLinks = (msg: ChatMessage) => resolveLinksForMessage(msg, props.linkPreview, linkPreviewCache);

  // Justified watch: reacting to external property assignment has no event-based alternative.
  watch(
    () => state.messages?.value,
    (next) => {
      if (next && Array.isArray(next)) {
        internalMessages.value = [...next];
        arrayModeActivated.value = true;
        for (const msg of internalMessages.value) resolveLinks(msg);
      }
    },
  );

  const groups = computed<ChatMessageGroup[]>(() => groupMessages(internalMessages.value, currentUser.value));

  const appendMessages = (msgs: ChatMessage | ChatMessage[]): void => {
    const list = Array.isArray(msgs) ? msgs : [msgs];
    arrayModeActivated.value = true;
    internalMessages.value = [...internalMessages.value, ...list];
    for (const msg of list) resolveLinks(msg);
  };

  const prependMessages = (msgs: ChatMessage[]): void => {
    arrayModeActivated.value = true;
    internalMessages.value = [...msgs, ...internalMessages.value];
    for (const msg of msgs) resolveLinks(msg);
  };

  const updateMessage = (id: string, updates: Partial<Pick<ChatMessage, "msg" | "status" | "streaming">>): void => {
    const msg = internalMessages.value.find((m) => m.id === id);
    if (!msg) return;
    const hadText = msg.msg;
    Object.assign(msg, updates);
    if (updates.msg && updates.msg !== hadText) resolveLinks(msg);
  };

  const clearMessages = (): void => {
    internalMessages.value = [];
  };

  return { isArrayMode, groups, appendMessages, prependMessages, updateMessage, clearMessages };
};
